import { Divider } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { isNil } from 'lodash'

import { DatePicker } from '@eastcoal/formily/components/DatePicker'
import { FormItem } from '@eastcoal/formily/components/FormItem'
import { Input } from '@eastcoal/formily/components/Input'
import { InputNumber } from '@eastcoal/formily/components/InputNumber'
import { RichEditor } from '@eastcoal/formily/components/RichEditor'
import { SchemaItem } from '@eastcoal/formily/components/SchemaForm/interface'

import { AreaCascader } from '@eastcoal/shared/@formily/AreaCascader'
import { ContractTemplateSelect } from '@eastcoal/shared/@formily/ContractTemplateSelect'
import { DictSelect } from '@eastcoal/shared/@formily/DictSelect'
import { KindAllNodeCascader } from '@eastcoal/shared/@formily/KindAllNodeCascader'
import { PricingMethodSelect } from '@eastcoal/shared/components/PricingMethodSelect'
import { AuctionCode } from '@eastcoal/shared/enums/AuctionCode'
import { BizType } from '@eastcoal/shared/enums/BizType'

import {
  KIND_FIELD,
  PRICING_METHOD_FIELD,
  TEMPLATE_FIELD
} from '../../constants/filed'
import { ContractTemplateTypeRadio } from '../components/ContractTemplateTypeRadio'
import { DepositInput } from '../components/DepositInput'
import { FormItemBlock } from '../components/FormItemBlock'
import { ListingPicker } from '../components/ListingPicker'
import { ListingQuotaList } from '../components/ListingQuotaList'

export const listingSchema: SchemaItem[] = [
  {
    title: '复制资源',
    type: 'void',
    'x-display': '{{$form.editable ? "visible":"hidden"}}',
    'x-decorator': FormItem,
    'x-decorator-props': {
      wrapperWidth: 400,
      feedbackText:
        '此为选填项，选择复制已发布资源后，将自动带入所选资源的全部字段信息'
    },
    'x-component': ListingPicker
  },
  {
    type: 'void',
    'x-display': '{{$form.editable ? "visible":"hidden"}}',
    'x-component': Divider
  },
  {
    title: '品种',
    type: 'void',
    'x-decorator': FormItem,
    'x-component': FormItemBlock,
    properties: {
      [KIND_FIELD]: {
        title: '品种',
        type: 'object',
        required: true,
        'x-component': KindAllNodeCascader,
        'x-decorator': FormItem
      }
    }
  },
  {
    title: '产品属性',
    type: 'void',
    'x-component': FormItemBlock,
    'x-decorator': FormItem,
    properties: {
      '{id: producerId,name: producerName,code: producerCode}': {
        type: 'object',
        title: '产地',
        required: true,
        'x-component': AreaCascader,
        'x-decorator': FormItem
      },
      listingItemList: {
        type: 'array',
        'x-component': ListingQuotaList
      }
    }
  },
  {
    title: '交易信息',
    type: 'void',
    'x-decorator': FormItem,
    'x-component': FormItemBlock,
    properties: {
      saleableWeight: {
        title: '可卖重量',
        type: 'number',
        required: true,
        'x-component': InputNumber,
        'x-component-props': {
          addonAfter: '吨'
        },
        'x-decorator': FormItem,
        'x-validator': {
          minimum: 1
        }
      },
      minimumTransaction: {
        title: '最小交易量',
        type: 'number',
        required: true,
        'x-component': InputNumber,
        'x-component-props': {
          addonAfter: '吨'
        },
        'x-decorator': FormItem,
        'x-validator': [
          { required: true, message: '最小交易量不能为空' },
          {
            minimum: 1,
            maximum: '{{$values.saleableWeight}}'
          }
        ] as any
      },
      publishPrice: {
        title: '产品单价',
        type: 'number',
        'x-component': InputNumber,
        'x-component-props': {
          addonAfter: '{{$values.priceUnit??"元/吨"}}'
        },
        'x-decorator': FormItem,
        'x-validator': {
          exclusiveMinimum: 0
        }
      },
      [PRICING_METHOD_FIELD]: {
        type: 'object',
        title: '计价方式',
        required: true,
        'x-decorator': FormItem,
        'x-component': PricingMethodSelect,
        'x-component-props': {
          supportTradeType: '1'
        }
      },
      validDate: {
        title: '有效期',
        type: 'string',
        'x-decorator': FormItem,
        'x-component': DatePicker,
        'x-component-props': {
          disabledDate: (currentDate: Dayjs) =>
            currentDate.isBefore(new Date(), 'date')
        },
        'x-validator': [
          {
            required: true
          },
          (value: string) => {
            if (value && dayjs(value).isBefore(new Date(), 'date')) {
              return '有效期不能小于当前时间'
            }
          }
        ]
      },
      '{depositQuantity,depositMethod}': {
        title: '保证金',
        type: 'object',
        required: true,
        'x-decorator': FormItem,
        'x-component': DepositInput,
        'x-validator': [
          {
            validator(value) {
              return isNil(value.depositQuantity) ? '保证金的值不能为空' : ''
            }
          }
        ]
      },
      paymentMethod: {
        title: '付款方式',
        type: 'string',
        required: true,
        'x-component': DictSelect,
        'x-component-props': {
          code: AuctionCode.PAYMENT_METHOD
        },
        'x-decorator': FormItem
      }
    }
  },
  {
    type: 'void',
    title: '交货信息',
    'x-decorator': FormItem,
    'x-component': FormItemBlock,
    properties: {
      deliveryMethod: {
        type: 'string',
        title: '交货方式',
        required: true,
        'x-component': DictSelect,
        'x-component-props': {
          code: AuctionCode.DELIVERY_METHOD
        },
        'x-decorator': FormItem
      },
      [`{
        id: deliveryPlaceId, 
        name: deliveryPlaceName, 
        code: deliveryPlaceCode
      }`]: {
        type: 'object',
        title: '交货地址',
        required: true,
        'x-component': AreaCascader,
        'x-decorator': FormItem
      },
      thirdPartyInspection: {
        type: 'string',
        title: '第三方验收机构',
        'x-component': Input,
        'x-decorator': FormItem
      }
    }
  },
  {
    type: 'void',
    title: '合同信息',
    'x-decorator': FormItem,
    'x-component': FormItemBlock,
    properties: {
      contractTemplateType: {
        title: '合同方式',
        type: 'boolean',
        default: '{{!!$values.templateId}}',
        'x-decorator': FormItem,
        'x-component': ContractTemplateTypeRadio
      },
      [TEMPLATE_FIELD]: {
        type: 'object',
        title: '合同模板',
        'x-display': '{{$values.contractTemplateType ? "visible":"hidden"}}',
        required: '{{$values.contractTemplateType }}',
        'x-decorator': FormItem,
        'x-component': ContractTemplateSelect,
        'x-component-props': {
          placeholder: '请选择合同模板',
          bizType: BizType.LISTING,
          memberId: '{{$values.memberId}}'
        }
      }
    }
  },
  {
    type: 'string',
    title: '资源描述',
    name: 'description',
    'x-decorator': FormItem,
    'x-component': RichEditor
  }
]
